import type { User } from "@overmap-ai/common"
import type { ReactNode } from "react"

import {
	UserEqualsFilterModifier,
	UserExcludesFilterModifier,
	UserIncludesFilterModifier,
	UserNotEqualsFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { CreatedByFilterCell } from "./CreatedByFilterCell"

type CreatedByFieldValue = User["id"]
type CreatedByFilterValue = User["id"][] | User["id"]

export class CreatedByFilterDef extends BaseFilterDef<CreatedByFieldValue, CreatedByFilterValue> {
	readonly id = "created_by"
	readonly defaultFilterValue: CreatedByFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"
	readonly users: User[]

	constructor(users: User[]) {
		super()
		this.users = users
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: UserEqualsFilterModifier,
			isValueValid: (_value: CreatedByFieldValue): _value is User["id"] => true,
			isFilterValueValid: (filterValue: CreatedByFilterValue): filterValue is User["id"] =>
				!Array.isArray(filterValue),
		}),
		notEquals: createFilterModifierConfig({
			modifier: UserNotEqualsFilterModifier,
			isValueValid: (_value: CreatedByFieldValue): _value is User["id"] => true,
			isFilterValueValid: (filterValue: CreatedByFilterValue): filterValue is User["id"] =>
				!Array.isArray(filterValue),
		}),
		includes: createFilterModifierConfig({
			modifier: UserIncludesFilterModifier,
			isValueValid: (_value: CreatedByFieldValue): _value is User["id"] => true,
			isFilterValueValid: (filterValue: CreatedByFilterValue): filterValue is User["id"][] =>
				Array.isArray(filterValue),
		}),
		excludes: createFilterModifierConfig({
			modifier: UserExcludesFilterModifier,
			isValueValid: (_value: CreatedByFieldValue): _value is User["id"] => true,
			isFilterValueValid: (filterValue: CreatedByFilterValue): filterValue is User["id"][] =>
				Array.isArray(filterValue),
		}),
	}

	render(props: FilterDefRenderProps<CreatedByFilterDef>): ReactNode {
		return <CreatedByFilterCell key={props.filter.id} {...props} />
	}
}
